import React, { Component } from "react";
import WithRoutify from "../../../hoc/WithRoutify";
import WithRouter from "../../../hoc/WithRouter";
import { decryptString } from "../../../utils/commonUtils";
import TableGrid from "../../../components/UI/TableGrid";
import SectionLoader from "../../../components/UI/SectionLoader";
import EmployeeService from "../../../services/EmployeeService";
import VisitsService from "../../../services/VisitsService";
import moment from "moment";
import { isSet } from "../../../utils/commonUtils";


class Images extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isModelOpen: false,
      groupedDocuments: [],
      expandedVisits: {},
      expandedActivities: {},
    };
  }

  fetchDocumentList = async () => {
    this.props.Loader(true);
    const { params } = this.props;
    try {
      // First get all visits for the employee
      const visitsResponse = await EmployeeService.getEmployeeVisitsHistoryList(params.patientId);
      
      if (!isSet(visitsResponse?.data?.data) || visitsResponse.data.data.length === 0) {
        this.setState({ groupedDocuments: [] });
        this.props.Loader(false);
        return;
      }

      // Group documents by visit and activity
      const groupedData = [];
      
      // Fetch documents for each visit using the employee-visits-documents API
      for (const visit of visitsResponse.data.data) {
        try {
          // Fetch documents for this visit using the API endpoint
          const documentsResponse = await VisitsService.getPhysicalAttachementList(visit.visit_id);
          
          if (!documentsResponse?.data || documentsResponse.data.length === 0) {
            continue; // Skip visits with no documents
          }

          const documents = documentsResponse.data;

          // Create a map of mapping_id to activity name from visit data
          const activityMap = {};
          
          // Check mapping_activity.data (paginated response)
          if (visit.mapping_activity?.data) {
            // Handle paginated collection - data might be an object with items or an array
            const mappingData = visit.mapping_activity.data.data || visit.mapping_activity.data;
            if (Array.isArray(mappingData)) {
              mappingData.forEach((mapping) => {
                const id = mapping.mapping_id || mapping.visit_mapping_id || mapping.id || mapping.physical_attachment_id;
                if (id && mapping.activity?.activity_name) {
                  activityMap[id] = mapping.activity.activity_name;
                }
              });
            }
          }
          
          // Check mappingActivity (direct relationship - might be unset in some cases)
          if (visit.mappingActivity && Array.isArray(visit.mappingActivity)) {
            visit.mappingActivity.forEach((mapping) => {
              const id = mapping.mapping_id || mapping.visit_mapping_id || mapping.id;
              if (id && mapping.activity?.activity_name) {
                activityMap[id] = mapping.activity.activity_name;
              }
            });
          }
          
          // Debug log to see what we're working with
          if (process.env.NODE_ENV === 'development') {
            console.log('Activity Map for visit:', {
              visit_id: visit.visit_id,
              activityMap: activityMap,
              mapping_activity_structure: visit.mapping_activity,
              documents_count: documents.length,
              first_doc_mapping_id: documents[0]?.visit_mapping_id
            });
          }

          // Group documents by visit_mapping_id (activity)
          const documentsByActivity = {};
          
          documents.forEach((doc) => {
            const mappingId = doc.visit_mapping_id || 'no_activity';
            // First try to get activity name from the document itself (if API includes it)
            let activityName = doc.activity?.activity_name || null;
            
            // If not in document, try to get from visit mapping data
            if (!activityName) {
              activityName = this.getActivityName(visit, mappingId, activityMap);
            }
            
            // If still not found and doc has activity_id, try to find by activity_id
            if ((!activityName || activityName === 'Activity') && doc.activity_id) {
              activityName = this.getActivityNameByActivityId(visit, doc.activity_id, activityMap);
            }
            
            // Debug if still not found
            if ((!activityName || activityName === 'Activity') && process.env.NODE_ENV === 'development') {
              console.log('Activity name not found for:', {
                mappingId: mappingId,
                activity_id: doc.activity_id,
                doc_activity: doc.activity,
                activityMap_keys: Object.keys(activityMap),
                visit_mapping_activity: visit.mapping_activity
              });
            }
            
            if (!documentsByActivity[mappingId]) {
              documentsByActivity[mappingId] = {
                mappingId: mappingId,
                activityName: activityName,
                documents: []
              };
            }
            
            documentsByActivity[mappingId].documents.push({
              rowId: doc.physical_attachment_id,
              data: {
                name: doc.snap_file_manager,
                updatedAt: moment.utc(doc.updated_at).format('MMMM D, YYYY h:mm:ss A'),
              }
            });
          });

          // Only add visit if it has documents
          if (Object.keys(documentsByActivity).length > 0) {
            const visitEntry = {
              visitId: visit.visit_id,
              visitDate: visit.visit_date || '--',
              visitTime: visit.visit_time || '--',
              visitCategory: visit.visit_category || '--',
              activities: Object.values(documentsByActivity)
            };

            groupedData.push(visitEntry);
          }
        } catch (error) {
          console.error(`Error fetching documents for visit ${visit.visit_id}:`, error);
          // Continue with next visit
        }
      }

      this.setState({ groupedDocuments: groupedData });
    } catch (e) {
      console.error("Error fetching documents:", e);
      this.setState({ groupedDocuments: [] });
    } finally {
      this.props.Loader(false);
    }
  }

  getActivityName = (visit, mappingId, activityMap = {}) => {
    if (mappingId === 'no_activity' || !mappingId) {
      return 'General Documents';
    }
    
    // First check the pre-built activity map
    if (activityMap[mappingId]) {
      return activityMap[mappingId];
    }
    
    // Try to find activity name from mapping_activity.data (paginated response)
    if (visit.mapping_activity?.data) {
      // Handle paginated collection - data might be an object with items or an array
      const mappingData = visit.mapping_activity.data.data || visit.mapping_activity.data;
      if (Array.isArray(mappingData)) {
        const activity = mappingData.find(
          (mapping) => {
            const id = mapping.mapping_id || mapping.visit_mapping_id || mapping.id;
            return id === mappingId;
          }
        );
        if (activity?.activity?.activity_name) {
          return activity.activity.activity_name;
        }
      }
    }
    
    // Try mappingActivity (direct relationship)
    if (visit.mappingActivity && Array.isArray(visit.mappingActivity)) {
      const activity = visit.mappingActivity.find(
        (mapping) => {
          const id = mapping.mapping_id || mapping.visit_mapping_id || mapping.id;
          return id === mappingId;
        }
      );
      if (activity?.activity?.activity_name) {
        return activity.activity.activity_name;
      }
    }
    
    // Try mapping_activity as direct array
    if (visit.mapping_activity && Array.isArray(visit.mapping_activity)) {
      const activity = visit.mapping_activity.find(
        (mapping) => {
          const id = mapping.mapping_id || mapping.visit_mapping_id || mapping.id;
          return id === mappingId;
        }
      );
      if (activity?.activity?.activity_name) {
        return activity.activity.activity_name;
      }
    }
    
    // If still not found, try to fetch from document's activity_id if available
    // This is a fallback - we'll need to enhance this if documents have activity_id
    
    // Return a more user-friendly fallback
    return `Activity`;
  }

  getActivityNameByActivityId = (visit, activityId, activityMap = {}) => {
    if (!activityId) {
      return 'General Documents';
    }
    
    // Check mapping_activity.data (paginated response)
    if (visit.mapping_activity?.data) {
      const mappingData = visit.mapping_activity.data.data || visit.mapping_activity.data;
      if (Array.isArray(mappingData)) {
        const activity = mappingData.find(
          (mapping) => mapping.activity_id === activityId
        );
        if (activity?.activity?.activity_name) {
          return activity.activity.activity_name;
        }
      }
    }
    
    // Try mappingActivity (direct relationship)
    if (visit.mappingActivity && Array.isArray(visit.mappingActivity)) {
      const activity = visit.mappingActivity.find(
        (mapping) => mapping.activity_id === activityId
      );
      if (activity?.activity?.activity_name) {
        return activity.activity.activity_name;
      }
    }
    
    return 'Activity';
  }

  toggleVisit = (visitId) => {
    this.setState((prevState) => ({
      expandedVisits: {
        ...prevState.expandedVisits,
        [visitId]: !prevState.expandedVisits[visitId]
      }
    }));
  }

  toggleActivity = (visitId, mappingId) => {
    const key = `${visitId}_${mappingId}`;
    this.setState((prevState) => ({
      expandedActivities: {
        ...prevState.expandedActivities,
        [key]: !prevState.expandedActivities[key]
      }
    }));
  }

  getTableHeaders = () => {
    return [
      { label: 'File Name', key: 'name', type: 'FileType' },
      { label: 'Updated Date', key: 'updatedAt', type: '' },
    ];
  }

  componentDidMount = () => {
    this.fetchDocumentList();
  }

  render() {
    const { groupedDocuments, expandedVisits, expandedActivities } = this.state;
    const patientDetails = decryptString(this.props.params.patientDetails);

    return (
      <>
        <div className="w-100 p-3">
          <div className="sh_cardBox h-auto shadow-none border">
            <div className="sh_cardHeader">
              <div className="form-group d-flex mb-0 justify-content-between align-items-center">
                <h6 className="text-sm mb-0">Documents - {patientDetails.patientName}</h6>
              </div>
            </div>
            <div className="sh_cardBody p-3">
              {groupedDocuments.length === 0 ? (
                <div className="text-center p-4">
                  <p className="text-muted">No documents found</p>
                </div>
              ) : (
                groupedDocuments.map((visit) => (
                  <div key={visit.visitId} className="mb-4">
                    {/* Visit Header */}
                    <div 
                      className="d-flex justify-content-between align-items-center p-2 mb-2"
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        cursor: 'pointer',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px'
                      }}
                      onClick={() => this.toggleVisit(visit.visitId)}
                    >
                      <div>
                        <strong>{visit.visitCategory}</strong>
                        <span className="ml-2 text-muted">
                          {visit.visitDate} {visit.visitTime}
                        </span>
                        <span className="ml-2 badge badge-secondary">
                          {visit.activities.length} {visit.activities.length === 1 ? 'Activity' : 'Activities'}
                        </span>
                      </div>
                      <i 
                        className={`fa fa-chevron-${expandedVisits[visit.visitId] ? 'down' : 'right'}`}
                        aria-hidden="true"
                      ></i>
                    </div>

                    {/* Activities List */}
                    {expandedVisits[visit.visitId] && (
                      <div className="ml-3">
                        {visit.activities.map((activity) => {
                          const activityKey = `${visit.visitId}_${activity.mappingId}`;
                          return (
                            <div key={activity.mappingId} className="mb-3">
                              {/* Activity Header */}
                              <div
                                className="d-flex justify-content-between align-items-center p-2 mb-2"
                                style={{
                                  backgroundColor: '#e9ecef',
                                  cursor: 'pointer',
                                  border: '1px solid #ced4da',
                                  borderRadius: '4px'
                                }}
                                onClick={() => this.toggleActivity(visit.visitId, activity.mappingId)}
                              >
                                <div>
                                  <strong>{activity.activityName}</strong>
                                  <span className="ml-2 badge badge-info">
                                    {activity.documents.length} {activity.documents.length === 1 ? 'Document' : 'Documents'}
                                  </span>
                                </div>
                                <i
                                  className={`fa fa-chevron-${expandedActivities[activityKey] ? 'down' : 'right'}`}
                                  aria-hidden="true"
                                ></i>
                              </div>

                              {/* Documents List */}
                              {expandedActivities[activityKey] && (
                                <div className="ml-3">
                                  <TableGrid
                                    tableHeaders={this.getTableHeaders()}
                                    tableRows={{ data: activity.documents }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default WithRouter(WithRoutify(Images));
